import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FETCH_COMMON_DETAILS_KEYS } from "../query";
import { Post } from "../../../utils/apiService";
import {
  AddUpdateAccessGroupBody,
  AddUpdateBuildingBody,
  AddUpdateCriticalNationalitiesBody,
  AddUpdateDepartmentBody,
  AddUpdateDynamicWorkflowBody,
  AddUpdateEmailTemplateBody,
  AddUpdateFRDeviceBody,
  AddUpdateGateBody,
  AddUpdateLocationBody,
  AddUpdateMeetingRoomBody,
  DeleteAccessGroupByIdBody,
  DeleteBuildingByIdBody,
  DeleteDepartmentByIdBody,
  DeleteDeviceByIdBody,
  DeleteGateByIdBody,
  DeleteLocationByIdBody,
  DeleteMeetingRoomByIdBody,
  EncryptDecryptParams,
  IsAccessGroupExists,
  IsBuildingExistsBody,
  IsDepartmentExistsBody,
  IsDeviceExistsBody,
  IsGateExistsBody,
  IsIPAddressExistsBody,
  IsLocationExistsBody,
  IsMeetingRoomExistsBody,
  SyncFRDeviceTimeAndNtpServerBody,
  SyncFRDevicesGroupScheduleByLocationBody,
  SyncFRLogServerUrlBody,
  TestActiveDirectoryConnectionBody,
  TestEmailBody,
  UpdateActiveDirectoryConfigBody,
  UpdateFaceRecognitionBody,
  UpdateFRDefaultGroupsBody,
  UpdateFRLogServerUrlBody,
  UpdateFRNTPServerBody,
  UpdateSMSConfigBody,
  UpdateValidatePhotoBody,
  UpdateVisitingHoursBody,
  SyncedFRRecognitionBody,
  UpdateEmailConfigBody,
  GetBuildingFileBody,
  GetMusterPointFileBody,
  AddUpdateMusterPointBody,
  DeleteMusterPointByIdBody,
  AddUpdateFinalPermitApprovalRecipientsBody,
  TestSmsBody,
  UpdateKioskCheckInFlowConfigBody,
} from "../interface";
import { FETCH_USER_DETAILS_KEYS } from "../../user/query";

const updateKioskCheckInFlowConfigAction = async (body: UpdateKioskCheckInFlowConfigBody) => {
  return Post({
    url: "/api/Configs/UpdateKioskConfig",
    body,
  });
};

export const useUpdateKioskCheckInFlowConfig = () => {
  return useMutation({
    mutationFn: async (body: UpdateKioskCheckInFlowConfigBody) => updateKioskCheckInFlowConfigAction(body),
  });
};

const AddUpdateFinalPermitApprovalRecipientsAction = async (body: AddUpdateFinalPermitApprovalRecipientsBody) => {
  return Post({
    url: "/api/Email/AddUpdateFinalPermitApprovalRecipients",
    body,
  });
};

export const useAddUpdateFinalPermitApprovalRecipients = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateFinalPermitApprovalRecipientsBody) =>
      AddUpdateFinalPermitApprovalRecipientsAction(body),
  });
};

const GetBuildingFileAction = async (body: GetBuildingFileBody) => {
  return Post({
    url: "/api/Buildings/GetFile",
    body,
  });
};

export const useGetBuildingFile = () => {
  return useMutation({
    mutationFn: async (body: GetBuildingFileBody) =>
      GetBuildingFileAction(body),
  });
};

const GetMusterPointFileAction = async (body: GetMusterPointFileBody) => {
  return Post({
    url: "/api/MusterPoints/GetFile",
    body,
  });
};

export const useGetMusterPointFile = () => {
  return useMutation({
    mutationFn: async (body: GetMusterPointFileBody) =>
      GetMusterPointFileAction(body),
  });
};

const pingAllDevicesAction = async () => {
  return Post({
    url: "/api/FR/PingAllDevices",
  });
};

export const usePingAllDevices = () => {
  return useMutation({
    mutationFn: async () => pingAllDevicesAction(),
  });
};

const addUserAttachmentAction = async (body: FormData) => {
  return Post({
    url: "/api/Users/AddAttachments",
    body,
    isFormData: true,
  });
};

export const useAddUserAttachment = () => {
  return useMutation({
    mutationFn: async (body: FormData) => addUserAttachmentAction(body),
  });
};

const encryptAction = async (params: EncryptDecryptParams) => {
  return Post({
    url: "/api/TekConfig/EncryptValue",
    params,
  });
};

export const useEncrypt = () => {
  return useMutation({
    mutationFn: async (params: EncryptDecryptParams) => encryptAction(params),
  });
};

const decryptAction = async (params: EncryptDecryptParams) => {
  return Post({
    url: "/api/TekConfig/DecryptValue",
    params,
  });
};

export const useDecrypt = () => {
  return useMutation({
    mutationFn: async (params: EncryptDecryptParams) => decryptAction(params),
  });
};

const addUpdateAccessGroupAction = async (body: AddUpdateAccessGroupBody) => {
  return Post({
    url: "/api/AccessGroups/AddUpdate",
    body,
  });
};

export const useAddUpdateAccessGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateAccessGroupBody) =>
      addUpdateAccessGroupAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_ACCESS_GROUPS],
      });
    },
  });
};

const addUpdateGateAction = async (body: AddUpdateGateBody) => {
  return Post({
    url: "/api/Gates/AddUpdate",
    body,
  });
};

export const useAddUpdateGate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateGateBody) => addUpdateGateAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_GATES],
      });
    },
  });
};

const deleteAccessGroupByIdAction = async (body: DeleteAccessGroupByIdBody) => {
  return Post({
    url: "/api/AccessGroups/Delete",
    body,
  });
};

export const useDeleteAccessGroupById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteAccessGroupByIdBody) =>
      deleteAccessGroupByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_ACCESS_GROUPS],
      });
    },
  });
};

const deleteGateByIdAction = async (body: DeleteGateByIdBody) => {
  return Post({
    url: "/api/Gates/Delete",
    body,
  });
};

export const useDeleteGateById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteGateByIdBody) => deleteGateByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_GATES],
      });
    },
  });
};

const isAccessGroupExists = async (body: IsAccessGroupExists) => {
  return Post({
    url: "/api/AccessGroups/IsExists",
    body,
  });
};

export const useIsAccessGroupExists = () => {
  return useMutation({
    mutationFn: async (body: IsAccessGroupExists) => isAccessGroupExists(body),
  });
};

const isGateExistsAction = async (body: IsGateExistsBody) => {
  return Post({
    url: "/api/Gates/IsExists",
    body,
  });
};

export const useIsGateExists = () => {
  return useMutation({
    mutationFn: async (body: IsGateExistsBody) => isGateExistsAction(body),
  });
};

const addUpdateEmailTemplateAction = async (
  body: AddUpdateEmailTemplateBody,
) => {
  return Post({
    url: "/api/EmailConfiguration/AddUpdateEmailTemplate",
    body,
  });
};

export const useAddUpdateEmailTemplate = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateEmailTemplateBody) =>
      addUpdateEmailTemplateAction(body),
  });
};

const isLocationExistsAction = async (body: IsLocationExistsBody) => {
  return Post({
    url: "/api/Locations/IsExists",
    body,
  });
};

export const useIsLocationExists = () => {
  return useMutation({
    mutationFn: async (body: IsLocationExistsBody) =>
      isLocationExistsAction(body),
  });
};

const deleteLocationByIdAction = async (body: DeleteLocationByIdBody) => {
  return Post({
    url: "/api/Locations/Delete",
    body,
  });
};

export const useDeleteLocationById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteLocationByIdBody) =>
      deleteLocationByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_LOCATIONS],
      });
    },
  });
};

const isMeetingRoomExistsAction = async (body: IsMeetingRoomExistsBody) => {
  return Post({
    url: "/api/MeetingRooms/IsExists",
    body,
  });
};

export const useIsMeetingRoomExists = () => {
  return useMutation({
    mutationFn: async (body: IsMeetingRoomExistsBody) =>
      isMeetingRoomExistsAction(body),
  });
};

const isDepartmentExistsAction = async (body: IsDepartmentExistsBody) => {
  return Post({
    url: "/api/Departments/IsExists",
    body,
  });
};

export const useIsDepartmentExists = () => {
  return useMutation({
    mutationFn: async (body: IsDepartmentExistsBody) =>
      isDepartmentExistsAction(body),
  });
};

const deleteMeetingRoomByIdAction = async (body: DeleteMeetingRoomByIdBody) => {
  return Post({
    url: "/api/MeetingRooms/Delete",
    body,
  });
};

export const useDeleteMeetingRoomById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteMeetingRoomByIdBody) =>
      deleteMeetingRoomByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_MEETING_ROOMS],
      });
    },
  });
};

const deleteDepartmentByIdAction = async (body: DeleteDepartmentByIdBody) => {
  return Post({
    url: "/api/Departments/Delete",
    body,
  });
};

export const useDeleteDepartmentById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteDepartmentByIdBody) =>
      deleteDepartmentByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_DEPARTMENTS],
      });
    },
  });
};

const addUpdateEmailConfigAction = async (body: UpdateEmailConfigBody) => {
  return Post({
    url: "/api/EmailConfiguration/UpdateEmailConfig",
    body,
  });
};

export const useUpdateEmailConfig = () => {
  return useMutation({
    mutationFn: async (body: UpdateEmailConfigBody) =>
      addUpdateEmailConfigAction(body),
  });
};

const testSmsAction = async (body: TestSmsBody) => {
  return Post({
    url: "/api/Configs/TestSMS",
    body,
  });
};

export const useTestSms = () => {
  return useMutation({
    mutationFn: async (body: TestSmsBody) => testSmsAction(body),
  });
};

const testEmailAction = async (body: TestEmailBody) => {
  return Post({
    url: "/api/EmailConfiguration/TestEmail",
    body,
  });
};

export const useTestEmail = () => {
  return useMutation({
    mutationFn: async (body: TestEmailBody) => testEmailAction(body),
  });
};

const updateSmsConfigAction = async (body: UpdateSMSConfigBody) => {
  return Post({
    url: "/api/Configs/UpdateSMSConfig",
    body,
  });
};

export const useUpdateSmsConfig = () => {
  return useMutation({
    mutationFn: async (body: UpdateSMSConfigBody) => updateSmsConfigAction(body),
  });
};

const updateActiveDirectoryConfigAction = async (
  body: UpdateActiveDirectoryConfigBody,
) => {
  return Post({
    url: "/api/Configs/UpdateActiveDirectoryConfig",
    body,
  });
};

export const useUpdateActiveDirectoryConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateActiveDirectoryConfigBody) =>
      updateActiveDirectoryConfigAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ACTIVE_DIRECTORY_CONFIG],
      });
    },
  });
};

const testActiveDirectoryConnectionAction = async (
  body: TestActiveDirectoryConnectionBody,
) => {
  return Post({
    url: "/api/ActiveDirectory/AuthenticateADUser",
    body,
  });
};

export const useTestActiveDirectoryConnection = () => {
  return useMutation({
    mutationFn: async (body: TestActiveDirectoryConnectionBody) =>
      testActiveDirectoryConnectionAction(body),
  });
};

const addUpdateLocationAction = async (body: AddUpdateLocationBody) => {
  return Post({
    url: "/api/Locations/AddUpdate",
    body,
  });
};

export const useAddUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateLocationBody) =>
      addUpdateLocationAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_LOCATIONS],
      });
    },
  });
};

const addUpdateMeetingRoomAction = async (body: AddUpdateMeetingRoomBody) => {
  return Post({
    url: "/api/MeetingRooms/AddUpdate",
    body,
  });
};

export const useAddUpdateMeetingRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateMeetingRoomBody) =>
      addUpdateMeetingRoomAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_MEETING_ROOMS],
      });
    },
  });
};

const addUpdateDepartmentAction = async (body: AddUpdateDepartmentBody) => {
  return Post({
    url: "/api/Departments/AddUpdate",
    body,
  });
};

export const useAddUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateDepartmentBody) =>
      addUpdateDepartmentAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_DEPARTMENTS],
      });
    },
  });
};

const addUpdateDeviceAction = async (body: AddUpdateFRDeviceBody) => {
  return Post({
    url: "/api/FRDevices/AddUpdate",
    body,
  });
};

export const useAddUpdateDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateFRDeviceBody) =>
      addUpdateDeviceAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_FR_DEVICES],
      });
    },
  });
};

const addBuildingAttachmentAction = async (body: FormData) => {
  return Post({
    url: "/api/Buildings/AddAttachments",
    body,
    isFormData: true,
  });
};

export const useAddBuildingAttachment = () => {
  return useMutation({
    mutationFn: async (body: FormData) => addBuildingAttachmentAction(body),
  });
};

const addUpdateBuildingAction = async (body: AddUpdateBuildingBody) => {
  return Post({
    url: "/api/Buildings/AddUpdate",
    body,
  });
};

export const useAddUpdateBuilding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateBuildingBody) =>
      addUpdateBuildingAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_BUILDINGS],
      });
    },
  });
};

const addUpdateMusterPointAction = async (body: AddUpdateMusterPointBody) => {
  return Post({
    url: "/api/MusterPoints/AddUpdate",
    body,
  });
};

export const useAddUpdateMusterPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateMusterPointBody) =>
      addUpdateMusterPointAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_MUSTER_POINTS],
      });
    },
  });
};

const isIPAddressExistsAction = async (body: IsIPAddressExistsBody) => {
  return Post({
    url: "/api/FRDevices/IsIpAddressExists",
    body,
  });
};

export const useIsIPAddressExists = () => {
  return useMutation({
    mutationFn: async (body: IsIPAddressExistsBody) =>
      isIPAddressExistsAction(body),
  });
};

const isDeviceExistsAction = async (body: IsDeviceExistsBody) => {
  return Post({
    url: "/api/FRDevices/IsExists",
    body,
  });
};

export const useIsDeviceExists = () => {
  return useMutation({
    mutationFn: async (body: IsDeviceExistsBody) => isDeviceExistsAction(body),
  });
};

const isBuildingExistsAction = async (body: IsBuildingExistsBody) => {
  return Post({
    url: "/api/Buildings/IsExists",
    body,
  });
};

export const useIsBuildingExists = () => {
  return useMutation({
    mutationFn: async (body: IsBuildingExistsBody) =>
      isBuildingExistsAction(body),
  });
};

const deleteDeviceByIdAction = async (body: DeleteDeviceByIdBody) => {
  return Post({
    url: "/api/FRDevices/Delete",
    body,
  });
};

export const useDeleteDeviceById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteDeviceByIdBody) =>
      deleteDeviceByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_FR_DEVICES],
      });
    },
  });
};

const deleteBuildingByIdAction = async (body: DeleteBuildingByIdBody) => {
  return Post({
    url: "/api/Buildings/Delete",
    body,
  });
};

export const useDeleteBuildingById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteBuildingByIdBody) =>
      deleteBuildingByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_BUILDINGS],
      });
    },
  });
};

const deleteMusterPointByIdAction = async (body: DeleteMusterPointByIdBody) => {
  return Post({
    url: "/api/MusterPoints/Delete",
    body,
  });
};

export const useDeleteMusterPointById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteMusterPointByIdBody) =>
      deleteMusterPointByIdAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_MUSTER_POINTS],
      });
    },
  });
};

const addUpdateDynamicWorkflowAction = async (
  body: AddUpdateDynamicWorkflowBody,
) => {
  return Post({
    url: "/api/DynamicWorkFlows/AddUpdate",
    body,
  });
};

export const useAddUpdateDynamicWorkflow = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateDynamicWorkflowBody) =>
      addUpdateDynamicWorkflowAction(body),
  });
};

const addHelpAttachmentAction = async (body: FormData) => {
  return Post({
    url: "/api/Help/Add",
    body,
    isFormData: true,
  });
};

export const useAddHelpAttachment = () => {
  return useMutation({
    mutationFn: async (body: FormData) => addHelpAttachmentAction(body),
  });
};

const getHelpAttachmentAction = async () => {
  return Post({
    url: "/api/Help/Get",
  });
};

export const useGetHelpAttachment = () => {
  return useMutation({
    mutationFn: async () => getHelpAttachmentAction(),
  });
};

const syncADUserAction = async () => {
  return Post({
    url: "/api/ActiveDirectory/SyncADUsers",
  });
};

export const useSyncADUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => syncADUserAction(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_USER_DETAILS_KEYS.FETCH_ALL_USERS],
      });
    },
  });
};

const syncDepartmentsAction = async () => {
  return Post({
    url: "/api/ActiveDirectory/SyncDepartments",
  });
};

export const useSyncDepartments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => syncDepartmentsAction(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_DEPARTMENTS],
      });
    },
  });
};

const addUpdateCriticalNationalitiesAction = async (
  body: AddUpdateCriticalNationalitiesBody,
) => {
  return Post({
    url: "/api/CriticalNationalities/AddUpdate",
    body,
  });
};

export const useAddUpdateCriticalNationalities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateCriticalNationalitiesBody) =>
      addUpdateCriticalNationalitiesAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_CRITICAL_NATIONALITIES],
      });
    },
  });
};

const updateFRLogServerUrlAction = async (body: UpdateFRLogServerUrlBody) => {
  return Post({
    url: "/api/Configs/UpdateFRLogServerUrlConfig",
    body,
  });
};

export const useUpdateFRLogServerUrl = () => {
  return useMutation({
    mutationFn: async (body: UpdateFRLogServerUrlBody) =>
      updateFRLogServerUrlAction(body),
  });
};

const updateVisitingHoursAction = async (body: UpdateVisitingHoursBody) => {
  return Post({
    url: "/api/Configs/UpdateVisitingHoursConfig",
    body,
  });
};

export const useUpdateVisitingHours = () => {
  return useMutation({
    mutationFn: async (body: UpdateVisitingHoursBody) =>
      updateVisitingHoursAction(body),
  });
};

const updateValidatePhotoAction = async (body: UpdateValidatePhotoBody) => {
  return Post({
    url: "/api/Configs/UpdateValidatePhotoConfig",
    body,
  });
};

export const useUpdateValidatePhoto = () => {
  return useMutation({
    mutationFn: async (body: UpdateValidatePhotoBody) =>
      updateValidatePhotoAction(body),
  });
};

const updateFRNTPServerAction = async (body: UpdateFRNTPServerBody) => {
  return Post({
    url: "/api/Configs/UpdateFRNTPServerConfig",
    body,
  });
};

export const useUpdateFRNTPServer = () => {
  return useMutation({
    mutationFn: async (body: UpdateFRNTPServerBody) =>
      updateFRNTPServerAction(body),
  });
};

const updateFRDefaultGroupsAction = async (body: UpdateFRDefaultGroupsBody) => {
  return Post({
    url: "/api/Configs/UpdateFRDefaultGroupsConfig",
    body,
  });
};

export const useUpdateFRDefaultGroups = () => {
  return useMutation({
    mutationFn: async (body: UpdateFRDefaultGroupsBody) =>
      updateFRDefaultGroupsAction(body),
  });
};

const updateFaceRecognitionAction = async (body: UpdateFaceRecognitionBody) => {
  return Post({
    url: "/api/Configs/UpdateFaceRecognitionConfig",
    body,
  });
};

export const useUpdateFaceRecognition = () => {
  return useMutation({
    mutationFn: async (body: UpdateFaceRecognitionBody) =>
      updateFaceRecognitionAction(body),
  });
};

const syncFRLogServerUrlAction = async (body: SyncFRLogServerUrlBody) => {
  return Post({
    url: "/api/FR/SyncedFRLogServerUrl",
    body,
  });
};

export const useSyncFRLogServerUrl = () => {
  return useMutation({
    mutationFn: async (body: SyncFRLogServerUrlBody) =>
      syncFRLogServerUrlAction(body),
  });
};

const syncFRDevicesGroupScheduleByLocationAction = async (
  body: SyncFRDevicesGroupScheduleByLocationBody,
) => {
  return Post({
    url: "/api/FR/SyncedFRDevicesGroupSchedule",
    body,
  });
};

export const useSyncFRDevicesGroupScheduleByLocation = () => {
  return useMutation({
    mutationFn: async (body: SyncFRDevicesGroupScheduleByLocationBody) =>
      syncFRDevicesGroupScheduleByLocationAction(body),
  });
};

const syncFRDeviceTimeAndNtpServerAction = async (
  body: SyncFRDeviceTimeAndNtpServerBody,
) => {
  return Post({
    url: "/api/FR/SyncedFRDeviceTimeAndNtpServer",
    body,
  });
};

export const useSyncFRDeviceTimeAndNtpServer = () => {
  return useMutation({
    mutationFn: async (body: SyncFRDeviceTimeAndNtpServerBody) =>
      syncFRDeviceTimeAndNtpServerAction(body),
  });
};

const syncedFRRecognitionAction = async (body: SyncedFRRecognitionBody) => {
  return Post({
    url: "/api/FR/SyncedFRRecognition",
    body,
  });
};

export const useSyncFRRecognition = () => {
  return useMutation({
    mutationFn: async (body: SyncedFRRecognitionBody) =>
      syncedFRRecognitionAction(body),
  });
};
